import React, {useEffect, useRef} from "react";

function TranscriptVisualization({miRNATargetPair, transcriptRegions, bindingPositions, transcriptAccessibility}) {
    const canvasRef = useRef(null);

    useEffect(() => {
        if (!transcriptRegions || transcriptRegions.length === 0 || !bindingPositions || !transcriptAccessibility) {
            console.warn("Invalid data: transcriptRegions, bindingPositions, or transcriptAccessibility is missing.");
            return;
        }

        const canvas = canvasRef.current;
        const ctx = canvas.getContext("2d");

        // Clear the canvas
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        const margin = 50;
        const width = canvas.width - 2 * margin;
        const startY = 150;

        // Colors and labels for regions
        const regionColors = ["darkgray", "black", "lightgray"];
        const regionLabels = ["5'UTR", "CDS", "3'UTR"];

        // Draw transcript regions
        let previousEnd = 0;
        transcriptRegions.forEach((end, index) => {
            const startX = margin + (previousEnd / transcriptRegions[transcriptRegions.length - 1]) * width;
            const endX = margin + (end / transcriptRegions[transcriptRegions.length - 1]) * width;

            ctx.beginPath();
            ctx.moveTo(startX, startY);
            ctx.lineTo(endX, startY);
            ctx.strokeStyle = regionColors[index % regionColors.length];
            ctx.lineWidth = 6; // transcript regions bar's height
            ctx.stroke();

            previousEnd = end;
        });

        const seedValues = ["4", "12", "125", "343", "490", "800", ">800"];

        // Define the seed color mapping
        const seedColors = [
            {range: [0, 4], color: "#ff0000"},     // Deep Red (Lowest)
            {range: [5, 12], color: "#ff4040"},    // Lighter Red
            {range: [13, 125], color: "#ff8080"},  // Pinkish Red
            {range: [126, 343], color: "#bf80ff"}, // Purple (Midway)
            {range: [344, 490], color: "#8080ff"}, // Light Blue
            {range: [491, 800], color: "#0000ff"}, // Deep Blue (for 491-800)
            {range: [801, 100000000], color: "#000000"} // Black (for >800)
        ];

        // Helper function to get color based on seed score
        const getSeedColor = (kd) => {
            const seedColor = seedColors.find(
                ({range}) => kd >= range[0] && kd <= range[1]
            );
            return seedColor ? seedColor.color : "#000000"; // Default to black if no match
        };

        // Draw interaction markers
        const totalLength = transcriptRegions[transcriptRegions.length - 1];
        const verticalStep = 30;

        const usedRanges = [];
        const horizontalThreshold = 15; // distance between two integer positions

        bindingPositions.forEach((position, index) => {
            const x = margin + (position / totalLength) * width;

            // Determine an appropriate y-coordinate to avoid overlap
            let y = startY - verticalStep;
            let collision = false;

            do {
                collision = false;
                for (const range of usedRanges) {
                    const overlapHorizontally =
                        x >= range.start - horizontalThreshold && x <= range.end + horizontalThreshold;
                    const overlapVertically = Math.abs(y - range.y) < verticalStep;

                    if (overlapHorizontally && overlapVertically) {
                        collision = true;
                        y -= verticalStep;
                        break;
                    }
                }
            } while (collision);

            // Register the new label range
            const labelWidth = ctx.measureText(position.toString()).width;
            usedRanges.push({start: x - labelWidth / 2, end: x + labelWidth / 2, y});

            // Draw interaction marker
            ctx.beginPath();
            ctx.moveTo(x, startY - 10);
            ctx.lineTo(x, startY + 10);
            ctx.strokeStyle = getSeedColor(seedValues[index]);
            ctx.lineWidth = 3; // width of the bars
            ctx.stroke();

            // Draw position label
            ctx.fillStyle = "black";
            ctx.font = "11px Arial"; // position labels size
            ctx.fillText(position, x - labelWidth / 2, y);
        });

        const legendStartY = startY + 50;
        const seedLegendStartY = legendStartY + 40;

        // Draw the interaction ID as a title above the legend
        const miRNATargetPairY = seedLegendStartY + -225;
        ctx.fillStyle = "black";
        ctx.font = "12px Arial";
        ctx.textAlign = "center";
        ctx.fillText(miRNATargetPair, canvas.width / 2, miRNATargetPairY);

        // Draw heatmap for transcriptAccessibility

        const heatmapY = miRNATargetPairY + 150; // Below the transcript  
        const segmentWidth = width / transcriptAccessibility.length;

        transcriptAccessibility.forEach((value, index) => {
            let color;
            if (value >= 0 && value <= 0.15) {
                color = "blue"; // Lowly accessible
            } else if (value > 0.15 && value <= 0.3) {
                color = "orange"; // Medium transcriptAccessibility                                                                                                          
            } else if (value > 0.3) {
                color = "red"; // Highly accessible                                                                                                                          
            } else {
                color = "gray"; // Default (unlikely to be used unless value is outside range)
            }

            ctx.fillStyle = color;
            const x = margin + index * segmentWidth;
            ctx.fillRect(x, heatmapY, segmentWidth, 6);
        });

    }, [miRNATargetPair, transcriptRegions, bindingPositions, transcriptAccessibility]);

    return (
        <div style={{display: "flex", flexDirection: "column", alignItems: "center", marginBottom: "20px", width: "100%"}}>
            {/* Left-Aligned Canvas */}
            <canvas
                ref={canvasRef}
                width={900}
                height={400}
                style={{display: "block", width: "100%", height: "auto"}}
            />

            {/* Legend Image Below the Visualization, Above Buttons */}
            <img
                src="/images/MiRScanLegend.png"
                alt="MiRScan Legend"
                style={{
                    width: "70%",
                    height: "auto",
                    marginTop: "-200px",
                    display: "block",
                    marginLeft: "auto",
                    marginRight: "auto"
                }}
            />
        </div>
    );
}

export default TranscriptVisualization;
